import { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import Button from 'uikit/Button/Button';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import { noCyrillicRegExp } from 'services/utils';
import './RequestDocumentModal.scss';

const DOCUMENT_TYPES = [
  { type: 'INVOICE', labelKey: 'transactionDetails.documentRequest.type.invoice' },
  { type: 'AGREEMENT', labelKey: 'transactionDetails.documentRequest.type.agreement' },
  { type: 'TRANSPORTATION_DOCUMENTS', labelKey: 'transactionDetails.documentRequest.type.transportationDocuments' },
  { type: 'PROOF_OF_WORK', labelKey: 'transactionDetails.documentRequest.type.proofOfWork' },
  { type: 'BANK_STATEMENT', labelKey: 'transactionDetails.documentRequest.type.bankStatement' },
  { type: 'TAX_DECLARATION', labelKey: 'transactionDetails.documentRequest.type.taxDeclaration' },
  { type: 'IDENTIFICATION_DOCUMENT', labelKey: 'transactionDetails.documentRequest.type.identificationDocument' },
  { type: 'PROOF_OF_ADDRESS', labelKey: 'transactionDetails.documentRequest.type.proofOfAddress' },
  { type: 'PROOF_OF_DIVIDENDS', labelKey: 'transactionDetails.documentRequest.type.proofOfDividends' },
  { type: 'PURPOSE_OF_PAYMENT', labelKey: 'transactionDetails.documentRequest.type.purposeOfPayment' },
  { type: 'OTHER', labelKey: 'transactionDetails.documentRequest.type.other' }
];

const RequestDocumentModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const i18n = useContext(i18nContext);
  const [selectedTypes, setSelectedTypes] = useState({});
  const [otherDescription, setOtherDescription] = useState('');
  const [error, setError] = useState('');
  const [hasCyrillicError, setHasCyrillicError] = useState(false);

  const handleTypeToggle = (type) => {
    setSelectedTypes((prev) => ({
      ...prev,
      [type]: !prev[type]
    }));
    setError('');
  };

  const handleOtherDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= 2000) {
      setOtherDescription(value);

      // Check for Cyrillic characters and set error
      if (value && !noCyrillicRegExp.test(value)) {
        setError(i18n.getMessage('transactionDetails.documentRequest.error.cyrillicNotAllowed'));
        setHasCyrillicError(true);
      } else if (error === i18n.getMessage('transactionDetails.documentRequest.error.cyrillicNotAllowed')) {
        // Clear error only if it was the Cyrillic error
        setError('');
        setHasCyrillicError(false);
      }
    }
  };

  const handleSubmit = () => {
    const selected = Object.keys(selectedTypes).filter((key) => selectedTypes[key]);

    if (selected.length === 0) {
      setError(i18n.getMessage('transactionDetails.documentRequest.error.noSelection'));
      return;
    }

    if (selected.includes('OTHER')) {
      if (!otherDescription.trim()) {
        setError(i18n.getMessage('transactionDetails.documentRequest.error.otherRequired'));
        return;
      }

      // Check for Cyrillic characters before submit
      if (!noCyrillicRegExp.test(otherDescription)) {
        setError(i18n.getMessage('transactionDetails.documentRequest.error.cyrillicNotAllowed'));
        return;
      }
    }

    const items = selected.map((type) => {
      if (type === 'OTHER') {
        return { type, name: otherDescription.trim() };
      }
      return { type };
    });

    onSubmit(items);
  };

  const handleClose = () => {
    setSelectedTypes({});
    setOtherDescription('');
    setError('');
    setHasCyrillicError(false);
    onClose();
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    <div className='request-document-modal-overlay' onClick={handleClose} onKeyDown={() => {}} role='presentation'>
      <div
        className='request-document-modal-content'
        onClick={(e) => e.stopPropagation()}
        onKeyDown={() => {}}
        role='presentation'
      >
        <div className='modal-header'>
          <div className='header-text'>
            <h2>{i18n.getMessage('transactionDetails.documentRequest.modal.title')}</h2>
            <p>{i18n.getMessage('transactionDetails.documentRequest.modal.subtitle')}</p>
          </div>
          <button className='close-button' onClick={handleClose}>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M18 6L6 18M6 6L18 18'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>

        <div className='modal-body'>
          <div className='document-types-list'>
            {DOCUMENT_TYPES.map(({ type, labelKey }) => (
              <div key={type} className='document-type-item'>
                <CheckBox
                  className='document-checkbox'
                  isActive={selectedTypes[type] || false}
                  isReverseView={true}
                  showCheckBox={true}
                  onChange={() => handleTypeToggle(type)}
                  value={i18n.getMessage(labelKey)}
                />
              </div>
            ))}
          </div>

          {selectedTypes.OTHER && (
            <div className='other-description-field'>
              <label htmlFor='other-description'>
                {i18n.getMessage('transactionDetails.documentRequest.customRequest')}
              </label>
              <div className='textarea-wrapper'>
                <textarea
                  id='other-description'
                  value={otherDescription}
                  onChange={handleOtherDescriptionChange}
                  placeholder={i18n.getMessage('transactionDetails.documentRequest.customRequestPlaceholder')}
                  rows={4}
                />
                <div className='character-count'>{otherDescription.length}/2000</div>
              </div>
            </div>
          )}

          {error && <div className='error-message'>{error}</div>}
        </div>

        <div className='modal-footer'>
          <Button type='outline' size='large' onClick={handleClose} isDisabled={isLoading}>
            {i18n.getMessage('transactionDetails.documentRequest.modal.cancel')}
          </Button>
          <Button
            type='primary'
            size='large'
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={isLoading || hasCyrillicError}
          >
            {i18n.getMessage('transactionDetails.documentRequest.modal.sendRequest')}
          </Button>
        </div>
      </div>
      {/* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
    </div>
  );
};

RequestDocumentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

export default RequestDocumentModal;
